/**
 * Unified Content API Client (Option A - Phase 2B)
 *
 * This module provides type-safe access to the unified content API endpoints
 * created in Week 3. All video-related endpoints are now under /api/content/{id}/*
 *
 * Key Features:
 * - Type-safe API calls with TypeScript
 * - Automatic retry logic for transient failures
 * - Consistent error handling
 * - Support for both UUID and slug identifiers
 */

import type {
  ContentDetailResponse,
  ContentTranscriptResponse,
  ContentOutlineResponse,
  ContentEntitiesResponse,
  ContentRelationshipsResponse,
  ContentAnalysisResponse,
  ContentQueryParams,
} from '../../../../shared/types/content-api';

import {
  buildApiUrl,
  processApiResponse,
  createAuthHeaders,
  withRetry,
  DEFAULT_RETRY_OPTIONS,
  APIError,
} from '../../../../shared/utils/api-helpers';

/**
 * Unified Content API Client
 *
 * Provides methods to interact with the unified content API endpoints.
 * All methods support both UUID and slug identifiers.
 */
export class ContentAPIClient {
  private baseUrl: string;
  private getToken: () => string | null | Promise<string | null>;

  constructor(baseUrl: string, getToken: () => string | null | Promise<string | null>) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
  }

  /**
   * Helper to resolve the token (handles both sync and async token getters)
   */
  private async resolveToken(): Promise<string | null> {
    const tokenOrPromise = this.getToken();
    return tokenOrPromise instanceof Promise ? await tokenOrPromise : tokenOrPromise;
  }

  // ============================================================================
  // Content Detail
  // ============================================================================

  /**
   * Get content detail by UUID or slug
   *
   * @param identifier - Content UUID or slug
   * @param params - Optional query parameters
   * @returns Content detail with related data
   *
   * @example
   * // By UUID
   * const content = await client.getContentDetail('123e4567-e89b-12d3-a456-426614174000');
   *
   * // By slug
   * const content = await client.getContentDetail('bitcoin-halving-explained-video-44');
   */
  async getContentDetail(
    identifier: string,
    params?: ContentQueryParams
  ): Promise<ContentDetailResponse> {
    const url = buildApiUrl(this.baseUrl, `/api/content/${identifier}`, params);
    const token = await this.resolveToken();
    const headers = createAuthHeaders(token);

    const fetchContent = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<ContentDetailResponse>(response);

      if (result.error) {
        throw new APIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchContent, DEFAULT_RETRY_OPTIONS);
  }

  // ============================================================================
  // Content Transcript
  // ============================================================================

  /**
   * Get video transcript by content UUID or slug
   *
   * @param identifier - Content UUID or slug
   * @returns Video transcript data
   * @throws APIError if content is not a video or transcript not found
   *
   * @example
   * const transcript = await client.getContentTranscript('bitcoin-halving-explained-video-44');
   */
  async getContentTranscript(identifier: string): Promise<ContentTranscriptResponse> {
    const url = buildApiUrl(this.baseUrl, `/api/content/${identifier}/transcript`);
    const token = await this.resolveToken();
    const headers = createAuthHeaders(token);

    const fetchTranscript = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<ContentTranscriptResponse>(response);

      if (result.error) {
        throw new APIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchTranscript, DEFAULT_RETRY_OPTIONS);
  }

  /**
   * Translate a non-English transcript to English (CUR-309)
   *
   * Idempotent: returns cached translation if one already exists.
   *
   * @param identifier - Content UUID or slug
   * @returns Updated transcript with translated_text populated
   */
  async translateTranscript(identifier: string): Promise<ContentTranscriptResponse> {
    const url = buildApiUrl(this.baseUrl, `/api/content/${identifier}/transcript/translate`);
    const token = await this.resolveToken();
    const headers = createAuthHeaders(token);

    const response = await fetch(url, { method: 'POST', headers });
    const result = await processApiResponse<ContentTranscriptResponse>(response);

    if (result.error) {
      throw new APIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // ============================================================================
  // Content Outline
  // ============================================================================

  /**
   * Get video outline by content UUID or slug
   *
   * Returns comprehensive analysis including Phase 1, 2A, 2B, and 2C fields.
   *
   * @param identifier - Content UUID or slug
   * @returns Video outline with all analysis fields
   * @throws APIError if content is not a video or outline not found
   *
   * @example
   * const outline = await client.getContentOutline('bitcoin-halving-explained-video-44');
   * console.log(outline.vital_statistics);
   * console.log(outline.temporal_metrics);
   */
  async getContentOutline(identifier: string): Promise<ContentOutlineResponse> {
    const url = buildApiUrl(this.baseUrl, `/api/content/${identifier}/outline`);
    const token = await this.resolveToken();
    const headers = createAuthHeaders(token);

    const fetchOutline = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<ContentOutlineResponse>(response);

      if (result.error) {
        throw new APIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchOutline, DEFAULT_RETRY_OPTIONS);
  }

  // ============================================================================
  // Content Entities
  // ============================================================================

  /**
   * Get video entities by content UUID or slug
   *
   * @param identifier - Content UUID or slug
   * @returns Video entities with metadata
   * @throws APIError if content is not a video or entities not found
   *
   * @example
   * const { video_id, entities } = await client.getContentEntities('bitcoin-halving-explained-video-44');
   * entities.forEach(entity => console.log(entity.entity_name, entity.entity_type));
   */
  async getContentEntities(identifier: string): Promise<ContentEntitiesResponse> {
    const url = buildApiUrl(this.baseUrl, `/api/content/${identifier}/entities`);
    const token = await this.resolveToken();
    const headers = createAuthHeaders(token);

    const fetchEntities = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<ContentEntitiesResponse>(response);

      if (result.error) {
        throw new APIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchEntities, DEFAULT_RETRY_OPTIONS);
  }

  // ============================================================================
  // Content Relationships
  // ============================================================================

  /**
   * Get video relationships by content UUID or slug
   *
   * @param identifier - Content UUID or slug
   * @returns Video relationships between entities
   * @throws APIError if content is not a video or relationships not found
   *
   * @example
   * const { video_id, relationships } = await client.getContentRelationships('bitcoin-halving-explained-video-44');
   * relationships.forEach(rel => console.log(rel.source_entity_id, '->', rel.target_entity_id));
   */
  async getContentRelationships(identifier: string): Promise<ContentRelationshipsResponse> {
    const url = buildApiUrl(this.baseUrl, `/api/content/${identifier}/relationships`);
    const token = await this.resolveToken();
    const headers = createAuthHeaders(token);

    const fetchRelationships = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<ContentRelationshipsResponse>(response);

      if (result.error) {
        throw new APIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchRelationships, DEFAULT_RETRY_OPTIONS);
  }

  // ============================================================================
  // Content Analysis (Comprehensive)
  // ============================================================================

  /**
   * Get comprehensive video analysis by content UUID or slug
   *
   * Returns all video data in a single response:
   * - Transcript
   * - Outline (with all Phase 1/2A/2B/2C fields)
   * - Entities
   * - Relationships
   *
   * @param identifier - Content UUID or slug
   * @returns Comprehensive video analysis
   * @throws APIError if content is not a video or analysis not found
   *
   * @example
   * const analysis = await client.getContentAnalysis('bitcoin-halving-explained-video-44');
   * console.log(analysis.transcript?.transcript_text);
   * console.log(analysis.outline?.vital_statistics);
   * console.log(analysis.entities.length, 'entities found');
   * console.log(analysis.relationships.length, 'relationships found');
   */
  async getContentAnalysis(identifier: string): Promise<ContentAnalysisResponse> {
    const url = buildApiUrl(this.baseUrl, `/api/content/${identifier}/analysis`);
    const token = await this.resolveToken();
    const headers = createAuthHeaders(token);

    const fetchAnalysis = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<ContentAnalysisResponse>(response);

      if (result.error) {
        throw new APIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchAnalysis, DEFAULT_RETRY_OPTIONS);
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Check if content is a video
   *
   * @param content - Content detail response
   * @returns True if content is a video
   */
  isVideo(content: ContentDetailResponse): boolean {
    return content.content_type === 'video';
  }

  /**
   * Check if content is an article
   *
   * @param content - Content detail response
   * @returns True if content is an article
   */
  isArticle(content: ContentDetailResponse): boolean {
    return content.content_type === 'article';
  }
}

// ============================================================================
// Default Content API Client Instance
// ============================================================================

import { config } from '@/utils/env';
import { getAuthToken } from '../auth/token-manager';

/**
 * Default Content API client instance
 *
 * Use this singleton instance throughout the application for content-related API calls.
 *
 * @example
 * import { contentApiClient } from '@/services/api/content-api';
 * const content = await contentApiClient.getContentDetail('video-slug-123');
 */
export const contentApiClient = new ContentAPIClient(config.apiUrl, getAuthToken);
